import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#8dd1e1', '#a4de6c', '#d0ed57'];

const Dashboard = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await axios.get('/api/stats/dashboard');
                setStats(response.data);
            } catch (error) {
                console.error("Error fetching dashboard stats", error);
                if (error.response && error.response.status === 403) {
                    alert("Accesso negato.");
                    navigate('/');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, [navigate]);

    const [showUserModal, setShowUserModal] = useState(false);
    const [userStats, setUserStats] = useState([]);

    const fetchUserStats = async () => {
        try {
            const response = await axios.get('/api/stats/users_activity');
            setUserStats(response.data);
            setShowUserModal(true);
        } catch (error) {
            console.error("Error fetching user stats", error);
            alert("Impossibile caricare i dettagli utenti.");
        }
    };

    if (loading) return <div style={{ padding: '2rem' }}>Caricamento dashboard...</div>;
    if (!stats) return <div style={{ padding: '2rem' }}>Errore nel caricamento dei dati.</div>;

    // Calcolo Totali per visualizzazione
    const totalVoto = (stats.voto_distribution || []).reduce((acc, curr) => acc + curr.value, 0);
    const totalStrict = (stats.voto_distribution_strict || []).reduce((acc, curr) => acc + curr.value, 0);

    return (
        <div className="page-container" style={{ padding: '2rem', backgroundColor: '#f4f6f9', minHeight: '100vh', position: 'relative' }}>
            <div className="header-container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <h1 style={{ color: '#333' }}>Dashboard Statistica</h1>
                <div className="header-actions" style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                    <button onClick={() => navigate('/')} style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Torna alla Chat</button>
                </div>
            </div>

            {/* Tastone — accesso alla Dashboard MultiFitting */}
            <button
                onClick={() => navigate('/dashboard-multifitting')}
                style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: '1rem',
                    padding: '1.1rem 1.5rem', marginBottom: '2rem', cursor: 'pointer',
                    border: 'none', borderRadius: '10px', textAlign: 'left',
                    background: 'linear-gradient(135deg, #007bff, #0056d6)', color: 'white',
                    boxShadow: '0 6px 20px rgba(0,123,255,0.25)',
                }}
            >
                <span style={{ fontSize: '1.6rem', lineHeight: 1 }}>📊</span>
                <span style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 }}>
                    <span style={{ fontSize: '1.05rem', fontWeight: 700 }}>Apri la Dashboard MultiFitting</span>
                    <span style={{ fontSize: '0.85rem', opacity: 0.9 }}>Ranking candidati, cluster e annunci · fitting cross-cluster</span>
                </span>
                <span style={{ fontSize: '1.4rem', fontWeight: 700 }}>→</span>
            </button>

            {/* Key Metrics */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <h3 style={{ margin: 0, color: '#6c757d', fontSize: '1rem' }}>Totale Candidati</h3>
                    <p style={{ margin: '0.5rem 0 0', fontSize: '2rem', fontWeight: 'bold', color: '#007bff' }}>{stats.total_candidates}</p>
                </div>
                <div
                    onClick={fetchUserStats}
                    style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', cursor: 'pointer', transition: 'transform 0.2s' }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    title="Clicca per dettagli utenti"
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <h3 style={{ margin: 0, color: '#6c757d', fontSize: '1rem' }}>Utenti Attivi</h3>
                            <p style={{ margin: '0.5rem 0 0', fontSize: '2rem', fontWeight: 'bold', color: '#28a745' }}>{stats.active_users}</p>
                        </div>
                        <div style={{ fontSize: '1.5rem', color: '#6c757d' }}>ℹ️</div>
                    </div>
                </div>
            </div>

            {/* Charts Row - Top */}
            <div className="dashboard-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '2rem' }}>

                {/* Voto CV Distribution */}
                <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <h3 style={{ marginBottom: '1.5rem', color: '#495057' }}>Distribuzione Voto CV (Totale: {totalVoto})</h3>
                    <div className="chart-container" style={{ height: '300px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={stats.voto_distribution}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                    outerRadius={100}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {stats.voto_distribution.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Year Distribution */}
                <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <h3 style={{ marginBottom: '1.5rem', color: '#495057' }}>Candidati per Anno Aggiornamento (Filtrati per Voto)</h3>
                    <div className="chart-container" style={{ height: '300px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={stats.year_distribution}
                                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="value" name="Candidati" fill="#82ca9d" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

            </div>

            {/* Divider / Title for Strict Analysis */}
            <div style={{ marginTop: '3rem', marginBottom: '1rem' }}>
                <h2 style={{ fontSize: '1.25rem', color: '#6c757d', borderBottom: '2px solid #e9ecef', paddingBottom: '0.5rem' }}>Analisi Qualificata (Con Manager & Costi)</h2>
            </div>

            {/* Charts Row - Strict */}
            <div className="dashboard-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '2rem', marginBottom: '2rem' }}>

                {/* Voto CV Distribution Strict */}
                <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <h3 style={{ marginBottom: '1.5rem', color: '#495057' }}>Distribuzione Voto Qualificati (Totale: {totalStrict})</h3>
                    <div className="chart-container" style={{ height: '300px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={stats.voto_distribution_strict || []}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                    outerRadius={100}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {(stats.voto_distribution_strict || []).map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Year Distribution Strict */}
                <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <h3 style={{ marginBottom: '1.5rem', color: '#495057' }}>Candidati per Anno (Qualificati per Manager & Costi)</h3>
                    <div className="chart-container" style={{ height: '300px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={stats.year_distribution_strict || []}
                                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="value" name="Candidati" fill="#ff7300" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

            </div>

            {showUserModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
                }} onClick={() => setShowUserModal(false)}>
                    <div style={{
                        backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', width: '95%', maxWidth: '800px', maxHeight: '85vh', overflowY: 'auto', display: 'flex', flexDirection: 'column'
                    }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexShrink: 0 }}>
                            <h2 style={{ margin: 0, fontSize: '1.25rem' }}>Dettaglio Attività Utenti</h2>
                            <button
                                onClick={() => setShowUserModal(false)}
                                style={{
                                    border: 'none', background: '#f8f9fa', borderRadius: '50%', width: '40px', height: '40px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '1.5rem', cursor: 'pointer', color: '#666'
                                }}
                            >
                                ×
                            </button>
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '500px' }}>
                                <thead>
                                    <tr style={{ borderBottom: '2px solid #eee' }}>
                                        <th style={{ textAlign: 'left', padding: '0.75rem' }}>Utente</th>
                                        <th style={{ textAlign: 'left', padding: '0.75rem' }}>Ultimo Accesso</th>
                                        <th style={{ textAlign: 'center', padding: '0.75rem' }}>Chat Create</th>
                                        <th style={{ textAlign: 'center', padding: '0.75rem' }}>Messaggi Totali</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {userStats.map((user, idx) => (
                                        <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                                            <td style={{ padding: '0.75rem' }}>
                                                <div style={{ fontWeight: 'bold' }}>{user.username}</div>
                                                {!user.is_active && <span style={{ fontSize: '0.8rem', color: 'red' }}>(Inattivo)</span>}
                                            </td>
                                            <td style={{ padding: '0.75rem' }}>
                                                {user.last_login ? new Date(user.last_login).toLocaleString() : 'Mai'}
                                            </td>
                                            <td style={{ padding: '0.75rem', textAlign: 'center' }}>{user.chat_count}</td>
                                            <td style={{ padding: '0.75rem', textAlign: 'center' }}>{user.message_count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;
